import { ChevronRight } from "lucide-react";
import { type RefObject, useRef } from "react";
import type { ModelRelease } from "../types/model";
import { useI18n } from "../hooks/use-i18n";
import { ui } from "../lib/localization";
import { CuratedSectionPanel } from "./CuratedSectionPanel";
import { ModelCard } from "./ModelCard";
import type { SmoothScrollController } from "./SmoothScroll";
import type { OrganizationIndex } from "../lib/organizations";
import type {
  ReleaseFilters,
  ReleaseTagFilterInteraction,
  ReleaseTagFilterKey
} from "../lib/query-models";

export interface CuratedModelSectionProps {
  variant: "flagship" | "milestone";
  records: ModelRelease[];
  open: boolean;
  visitCutoff: number;
  onToggle: () => void;
  onOpen: (record: ModelRelease) => void;
  onMotionComplete: () => void;
  scrollElementRef: RefObject<HTMLElement | null>;
  scrollControllerRef: RefObject<SmoothScrollController | null>;
  organizations: OrganizationIndex;
  filters: ReleaseFilters;
  onToggleFilter: (
    key: ReleaseTagFilterKey,
    value: string,
    interaction?: ReleaseTagFilterInteraction
  ) => void;
}

export function CuratedModelSection({
  variant,
  records,
  open,
  visitCutoff,
  onToggle,
  onOpen,
  onMotionComplete,
  scrollElementRef,
  scrollControllerRef,
  organizations,
  filters,
  onToggleFilter
}: CuratedModelSectionProps) {
  const { language } = useI18n();
  const expandFromTop = useRef<number | null>(null);

  if (!records.length) return null;

  const toggleLabel = variant === "flagship"
    ? open ? "collapseFlagship" : "expandFlagship"
    : open ? "collapseMilestone" : "expandMilestone";

  return (
    <section
      className={`curated-section curated-section--${variant}${open ? " curated-section--open" : ""}`}
      aria-labelledby={`${variant}-section-title`}
    >
      <button
        className="curated-section__toggle"
        type="button"
        aria-expanded={open}
        aria-controls={`${variant}-model-grid`}
        aria-label={ui(language, toggleLabel)}
        onClick={(event) => {
          if (!open) {
            expandFromTop.current = event.currentTarget.getBoundingClientRect().top;
          }
          onToggle();
        }}
      >
        <span id={`${variant}-section-title`}>{ui(language, variant)}</span>
        <i aria-hidden="true" />
        <ChevronRight aria-hidden="true" size={15} strokeWidth={1.8} />
      </button>

      <CuratedSectionPanel
        id={`${variant}-model-grid`}
        open={open}
        onMotionComplete={onMotionComplete}
        scrollElementRef={scrollElementRef}
        scrollControllerRef={scrollControllerRef}
        expandFromTopRef={expandFromTop}
      >
        <div className="model-grid curated-section__grid">
          {records.map((record) => {
            const releaseTime = record.dates.start
              ? Date.parse(`${record.dates.start}T00:00:00Z`)
              : 0;
            return (
              <ModelCard
                key={record.id}
                record={record}
                organizations={organizations}
                showDate={false}
                isNew={releaseTime > visitCutoff}
                keyboardActive={false}
                onOpen={onOpen}
                filters={filters}
                onToggleFilter={onToggleFilter}
              />
            );
          })}
        </div>
      </CuratedSectionPanel>
    </section>
  );
}
